export const name = "ST_BOUNDINGBOX";
export const summary = `Gets the bounding box for a \`GEOGRAPHY\` value.`;
export const reference = `
## ST_BOUNDINGBOX

Gets the bounding box for a \`GEOGRAPHY\` value.

\`\`\`
ST_BOUNDINGBOX(geography_expression)
\`\`\`

### Description

Returns a \`STRUCT\` that represents the bounding box for the specified geography. The bounding box is the minimal rectangle that encloses the geography. The edges of the rectangle follow constant lines of longitude and latitude.

Caveats:

- Returns \`NULL\` if the input is \`NULL\` or an empty geography.
- The bounding box might cross the antimeridian if this allows for a smaller rectangle. In this case, the bounding box has one of its longitudinal bounds outside of the [-180, 180] range, so that \`xmin\` is smaller than the eastmost value \`xmax\`.


### Return type

\`STRUCT<xmin FLOAT64, ymin FLOAT64, xmax FLOAT64, ymax FLOAT64>\`.

Bounding box parts:

- \`xmin\`: The westmost constant longitude line that bounds the rectangle.
- \`xmax\`: The eastmost constant longitude line that bounds the rectangle.
- \`ymin\`: The minimum constant latitude line that bounds the rectangle.
- \`ymax\`: The maximum constant latitude line that bounds the rectangle.


### Example



\`\`\`
WITH data AS (
  SELECT 1 id, ST_GEOGFROMTEXT('POLYGON((-125 48, -124 46, -117 46, -117 49, -125 48))') g
  UNION ALL
  SELECT 2 id, ST_GEOGFROMTEXT('POLYGON((172 53, -130 55, -141 70, 172 53))') g
  UNION ALL
  SELECT 3 id, ST_GEOGFROMTEXT('POINT EMPTY') g
  UNION ALL
  SELECT 4 id, ST_GEOGFROMTEXT('POLYGON((172 53, -141 70, -130 55, 172 53))', oriented => TRUE)
)
SELECT id, ST_BOUNDINGBOX(g) AS box
FROM data

/*----+------------------------------------------*
 | id | box |
 +----+------------------------------------------+
 | 1 | {xmin:-125, ymin:46, xmax:-117, ymax:49} |
 | 2 | {xmin:172, ymin:53, xmax:230, ymax:70} |
 | 3 | NULL |
 | 4 | {xmin:-180, ymin:-90, xmax:180, ymax:90} |
 *----+------------------------------------------*/
\`\`\`

See \`ST_EXTENT\` for the aggregate version of \`ST_BOUNDINGBOX\`.


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/geography_functions
`;
